import React, { useState, useEffect } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { businessStaffService } from '@/services/normalize-user.service';
import { api } from '@/services/api';
import { createServerSupabaseClient, getSessionFromSSR, getCurrentUserFromSSR } from '@/services/supabase';
import { BUSINESS_ROLE_PERMISSIONS, SUPER_ADMIN_PERMISSIONS } from '@/types/roles';
import type { BusinessRole, UserRole } from '@/types/roles';
import type { BusinessStaff } from '@/types/business';
import type { Business } from '@/types/business';
import { useNavigate } from 'react-router-dom';
import { createUpdateSession } from '@/services/stripe';
import { useDevMode } from '@/utils/environment';

// Server-side function that can be used with Next.js getServerSideProps
export async function getBusinessSettingsDataSSR(context: any) {
  try {
    // Create a server-side Supabase client
    const supabaseClient = createServerSupabaseClient(context);
    
    // Get the session using the SSR method
    const session = await getSessionFromSSR(supabaseClient);
    
    // If no session, return null
    if (!session) {
      return { user: null, data: null };
    }
    
    // Get the user using the SSR method
    const user = await getCurrentUserFromSSR(supabaseClient);
    
    // Check if user has proper permissions
    if (user) {
      const userRole = user.role as UserRole || 'business';
      const isBusiness = userRole === 'business';
      const isSuperAdmin = userRole === 'super_admin';
      
      if (!(isBusiness || isSuperAdmin)) {
        return { user: null, data: null };
      }
    } else {
      return { user: null, data: null };
    }
    
    // In a real implementation, we would fetch staff data from API
    // For now, we'll use mock data
    const mockStaff = (user as any)?.staff || [];
    const mockBusiness = {
      id: '123',
      name: 'Ocean Harvest Seafood',
      address: '123 Harbor Way, Seattle, WA',
      phone: '(206) 555-1234',
      email: 'contact@oceanharvest.com',
      website: 'www.oceanharvest.com',
      logo: '/logo.png',
      description: 'Sustainable seafood direct from local fishermen',
      staff: mockStaff
    }; // Add other business fields as needed
    
    return {
      user,
      data: mockBusiness
    };
  } catch (error) {
    console.error('Error in getBusinessSettingsDataSSR:', error);
    return { user: null, data: null };
  }
}

// Props interface for BusinessSettings component to accept SSR data
interface BusinessSettingsProps {
  initialUser?: any;
  initialData?: any;
}

const BusinessSettings: React.FC<BusinessSettingsProps> = ({ initialUser, initialData }) => {
  const { user, updateUser } = useAuthV2();
  const navigate = useNavigate();
  const isDevEnvironment = useDevMode();
  const [] = useState(!initialData);
  const [business] = useState<any>(initialData || null);
  const [] = useState<BusinessStaff[]>((initialData?.staff || []) as BusinessStaff[]);
  const [] = useState('');
  const [] = useState<BusinessRole>('staff');
  const [] = useState<string | null>(null);
  const [] = useState<BusinessRole>('staff');

  const currentStaff = user?.staff;
  // If user is a super admin, treat them as an owner, otherwise use their assigned role or default to staff
  const isSuperAdmin = user?.role === 'super_admin';
  const role = isSuperAdmin ? 'owner' as BusinessRole : (currentStaff?.role || 'staff') as BusinessRole;
  const permissions = isSuperAdmin ? SUPER_ADMIN_PERMISSIONS : BUSINESS_ROLE_PERMISSIONS[role];
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Separate states for staff data and business data
  const [staffData, setStaffData] = useState<Partial<BusinessStaff>>({
    staff_id: currentStaff?.staff_id || '',
    user_id: currentStaff?.user_id || '',
    business_id: currentStaff?.business_id || '',
    role: currentStaff?.role || 'staff',
    assigned_at: currentStaff?.assigned_at || '',
    created_at: currentStaff?.created_at,
    updated_at: currentStaff?.updated_at,
    email: user?.email || '',
    first_name: currentStaff?.first_name || '',
    last_name: currentStaff?.last_name || '',
    phone_number: currentStaff?.phone_number || '',
  });
  
  const [businessData, setBusinessData] = useState<Business | null>(null);

  // Update staff data when user data changes and fetch business data
  useEffect(() => {
    // If we have initialData and initialUser from SSR, use them directly
    if (initialData && initialUser) {
      setBusinessData(initialData);
      
      if (initialUser?.staff) {
        setStaffData({
          staff_id: initialUser.staff.staff_id,
          user_id: initialUser.staff.user_id,
          business_id: initialUser.staff.business_id,
          role: initialUser.staff.role,
          assigned_at: initialUser.staff.assigned_at,
          created_at: initialUser.staff.created_at,
          updated_at: initialUser.staff.updated_at,
          email: initialUser.email,
          first_name: initialUser.staff.first_name,
          last_name: initialUser.staff.last_name,
          phone_number: initialUser.staff.phone_number,
        });
      }
      
      setIsLoading(false);
    } else if (user?.staff) {
      // Otherwise use the existing client-side method
      setStaffData({
        staff_id: user.staff.staff_id,
        user_id: user.staff.user_id,
        business_id: user.staff.business_id,
        role: user.staff.role,
        assigned_at: user.staff.assigned_at,
        created_at: user.staff.created_at,
        updated_at: user.staff.updated_at,
        email: user.email,
        first_name: user.staff.first_name,
        last_name: user.staff.last_name,
        phone_number: user.staff.phone_number,
      });
      
      // Fetch business data if user is authenticated
      if (user.staff) {
        fetchBusinessData();
      } else {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [user]);
  
  // Fetch business information
  const fetchBusinessData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // Use the business service to get the profile data
      const data = await business.profile.get();
      setBusinessData(data);
    } catch (err) {
      console.error('Error fetching business data:', err);
      setError('Failed to load business information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStaffChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setStaffData(prev => ({ ...prev, [name]: value }));
  };
  

  const handleUpdatePaymentMethod = async () => {
    if (!user?.staff?.business_id) {
      setError('Business ID is required to update payment method');
      return;
    }

    try {
      setIsLoading(true);
      
      // Use the Stripe service to create a checkout session for updating payment method
      const sessionData = await createUpdateSession(user.staff.business_id);
      
      // Redirect to Stripe Checkout
      if (sessionData?.sessionUrl) {
        window.location.href = sessionData.sessionUrl;
      } else {
        throw new Error('No session URL returned');
      }
    } catch (err) {
      console.error('Error updating payment method:', err);
      setError('Failed to update payment method');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user?.staff?.staff_id) return;
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Only update the staff fields that are allowed to be updated
      const staffUpdates: Partial<BusinessStaff> = {
        first_name: staffData.first_name,
        last_name: staffData.last_name,
        phone_number: staffData.phone_number,
      };
      
      // Update the staff member using the business service
      // We need to use a different method since 'update' doesn't exist
      // First update the role if it's included in staffUpdates
      if (staffUpdates.role) {
        await businessStaffService.updateStaffRole(user.staff.user_id, user.staff.business_id, staffUpdates.role);
      }
      
      if (staffData.email && staffData.email !== user.email) {
        await updateUser({
          email: staffData.email
        });
      }
      
      // Update business data if available
      if (businessData && user.staff.business_id) {
        await api.put(`/api/business/${user.staff.business_id}`, businessData);
      }
      
      // Show success message
      alert('Profile updated successfully');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate(isDevEnvironment ? '/dev/business/dashboard' : '/business/dashboard')}
              className="mr-4 text-gray-500 hover:text-navy-blue"
            >
             
            </button>
            <h1 className="text-xl font-medium text-navy-blue">Business Settings</h1>
          </div>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          {/* Profile Section */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Staff ID</label>
                <input
                  id="staff_id"
                  name="staff_id"
                  value={staffData.staff_id}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  id="role"
                  name="role"
                  value={staffData.role}
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="volunteer">Volunteer</option>
                </select>
              </div>

              {(permissions.canModifyBusinessDetails || role === 'owner' || role === 'manager') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={staffData.email || ''}
                    onChange={handleStaffChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={staffData.first_name || ''}
                  onChange={handleStaffChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={staffData.last_name || ''}
                  onChange={handleStaffChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                <input
                  type="tel"
                  name="phone_number"
                  value={staffData.phone_number || ''}
                  onChange={handleStaffChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-blue focus:ring-sky-blue"
                />
              </div>
            </div>
          </div>

          {/* Business Information Section */}
          {permissions.canModifyBusinessDetails && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Business Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created At</label>
                  <input
                    id="created_at"
                    name="created_at"
                    type="text"
                    value={staffData.created_at ? new Date(staffData.created_at).toLocaleString() : 'N/A'}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Updated At</label>
                  <input
                    id="updated_at"
                    name="updated_at"
                    type="text"
                    value={staffData.updated_at ? new Date(staffData.updated_at).toLocaleString() : 'N/A'}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Business ID</label>
                  <input
                    id="business_id"
                    name="business_id"
                    value={staffData.business_id}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <input
                    id="user_id"
                    name="user_id"
                    value={staffData.user_id}
                    disabled
                    className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods Section */}
          {permissions.canModifyBilling && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Payment Methods</h2>
              <div className="space-y-4">
                <p className="text-gray-600">
                  Manage your business payment methods and billing information.
                </p>
                <button
                  type="button"
                  onClick={handleUpdatePaymentMethod}
                  className="bg-sky-blue text-white px-4 py-2 rounded-md hover:bg-sky-blue-dark focus:outline-none focus:ring-2 focus:ring-sky-blue focus:ring-offset-2"
                >
                  Update Payment Method
                </button>
              </div>
            </div>
          )}
          
          <div className="pt-5">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessSettings;
